import { getApiUrl } from './config';
import { getAuthHeader, getAuthToken, getRefreshToken, refreshTokenAndSave, clearAuthData } from '../services/authService';
import axios from 'axios';

/**
 * Hàm tiện ích để gọi API có xác thực với khả năng tự động refresh token
 * @param url Đường dẫn API
 * @param options Options cho fetch API
 * @param shouldRefresh Có nên tự động refresh token nếu hết hạn (mặc định là true)
 * @returns Kết quả từ API
 */
export const fetchWithAuth = async (
  url: string,
  options: RequestInit = {},
  shouldRefresh: boolean = true
): Promise<any> => {
  // Thêm Authorization header nếu chưa có
  if (!options.headers) {
    options.headers = {};
  }
  
  // Đảm bảo headers là một đối tượng
  const headers = options.headers as Record<string, string>;
  
  // Nếu không có Authorization header, thêm vào
  if (!headers.Authorization) {
    const authHeader = getAuthHeader();
    if (authHeader) {
      headers.Authorization = authHeader;
    }
  }
  
  try {
    // Thực hiện gọi API
    const response = await fetch(getApiUrl(url), options);
    
    // Nếu lỗi 401 Unauthorized hoặc 403 Forbidden và chúng ta nên refresh token
    if ((response.status === 401 || response.status === 403) && shouldRefresh) {
      console.log('Token hết hạn hoặc không có quyền. Đang làm mới token...');
      
      // Thử refresh token
      const refreshResult = await refreshTokenAndSave();
      
      if (refreshResult.result === 'SUCCESS' && refreshResult.data?.token) {
        console.log('Đã làm mới token thành công. Thử lại request...');
        
        // Cập nhật Authorization header với token mới
        const newAuthHeader = getAuthHeader();
        
        if (newAuthHeader) {
          headers.Authorization = newAuthHeader;
        }
        
        // Thực hiện lại request ban đầu
        return fetch(getApiUrl(url), options).then(res => res.json());
      } else {
        console.error('Không thể làm mới token:', refreshResult.message);
        throw new Error('Token hết hạn và không thể làm mới');
      }
    }
    
    // Trả về dữ liệu JSON
    return response.json();
  } catch (error) {
    console.error('Lỗi khi gọi API:', error);
    throw error;
  }
};

/**
 * Tạo headers với xác thực
 * @returns Headers với Authorization
 */
export const createAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  const authHeader = getAuthHeader();
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  
  return headers;
};

// Cấu hình axios với headers mặc định
axios.defaults.baseURL = getApiUrl('');
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Thiết lập các interceptors
const setupAxiosInterceptors = () => {
  // Request interceptor - thêm auth token vào mọi request
  axios.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - xử lý token hết hạn
  let isRefreshing = false;
  let failedQueue: any[] = [];

  const processQueue = (error: any, token: string | null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    
    failedQueue = [];
  };

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      
      // Nếu không có response hoặc không phải lỗi xác thực, reject luôn
      if (!error.response || (error.response.status !== 401 && error.response.status !== 403)) {
        return Promise.reject(error);
      }

      // Nếu đây là request refresh token bị lỗi, xóa token và reject
      if (originalRequest.url?.includes('refresh-token') || originalRequest._retry) {
        console.log('Refresh token bị lỗi hoặc request đã thử refresh token');
        // Xóa token và thông báo lỗi
        clearAuthData();
        return Promise.reject(error);
      }
      
      originalRequest._retry = true;

      if (isRefreshing) {
        // Nếu đang refresh, thêm request này vào queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      isRefreshing = true;

      try {
        console.log('Token hết hạn, đang refresh token...');
        const refreshResult = await refreshTokenAndSave();
        
        if (refreshResult.result === 'SUCCESS' && refreshResult.data?.token) {
          console.log('Refresh token thành công, thử lại request');
          const newToken = refreshResult.data.token;
          
          // Cập nhật token trong header mặc định
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          
          // Xử lý queue các requests bị treo
          processQueue(null, newToken);
          
          // Thực hiện lại request ban đầu
          return axios(originalRequest);
        } else {
          console.error('Không thể refresh token:', refreshResult.message);
          // Xóa token và thông báo cho queue
          clearAuthData();
          processQueue(new Error('Không thể refresh token'), null);
          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.error('Lỗi khi refresh token:', refreshError);
        // Xóa token và thông báo cho queue
        clearAuthData();
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

// Gọi hàm thiết lập interceptors
setupAxiosInterceptors();

export default {
  fetchWithAuth,
  createAuthHeaders
}; 